
import { Component, Inject, Vue, Watch, Prop } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import ViewCell from "@/components/views/ViewCell.vue";
import { ViewMode, ViewObject } from "@/store/views/types.ts";
import VueGridLayout from "@sureview/vue-grid";
import { get } from "lodash";
import ViewsEventBus from "@/services/views/viewsEventBus";

const ViewState = namespace("views");

@Component({
	components: {
		"grid-layout": VueGridLayout.GridLayout,
		"grid-item": VueGridLayout.GridItem,
		"view-cell": ViewCell
	}
})
export default class ViewController extends Vue {
	@ViewState.Action deleteView: any;

	@ViewState.Mutation setSelectedViewCells: any;
	@ViewState.Mutation setSelectedTileType: any;

	@ViewState.Getter("getSelectedView") selectedView: ViewObject;
	@ViewState.Getter getSelectedTileType: any;
	@ViewState.Getter getViewMode: ViewMode;

	@Prop({ type: Number, default: 0 }) private gridHeight: number;
	@Prop({ type: Number, default: 0 }) private gridWidth: number;

	@Inject("viewsEventBus") private eventBus!: ViewsEventBus;

	private viewCells: any[] = [];
	private cellToAdd: Object = null;
	private transientCellId: number = -Math.floor(100000 + Math.random() * 900000);
	private cellIdToFullscreen: number = 0;

	/** Gets the (rounded) height of each row in pixels */
	public get scaledRowHeight() {
		return Math.round(this.gridHeight / (this.selectedView.height || 20));
	}

	/**
	 * Gets the (rounded) width of each column in pixels.
	 */
	public get scaledColumnWidth() {
		return Math.round(this.gridWidth / (this.selectedView.width || 5));
	}

	/**
	 * Gets the (un-rounded) width of each column in pixels.
	 */
	public get columnWidth() {
		return this.gridWidth / (this.selectedView.width || 5);
	}

	/**
	 * Returns true if the user is currently editing the View.
	 */
	public get editing() {
		return this.getViewMode === ViewMode.Edit;
	}

	/**
	 * Generates shortcuts for selecting a view cell via the keyboard (Alt + 0-9).
	 */
	public get viewsShortcuts() {
		// Sort cells by their position within the columns/rows (horizontal -> vertically),
		// taking only the first 10.
		let viewCells = [...this.viewCells];
		let cells = viewCells
			.sort((cell1, cell2) => {
				if (cell1.y < cell2.y) {
					return -1;
				} else if (cell1.y > cell2.y) {
					return 1;
				}

				if (cell1.x < cell2.x) {
					return -1;
				} else if (cell1.x > cell2.x) {
					return 1;
				}

				return 0;
			})
			.slice(0, 9);

		let shortcuts = {};

		// Map the cells to shortcuts beginning with Alt
		cells.forEach((cell, idx) => {
			shortcuts[`cell-${cell.viewCellId}`] = ["alt", `${idx + 1}`];
		});

		return shortcuts;
	}

	/**
	 * Handles the press of a views shortcut.
	 */
	public handleShortcut(event) {
		let key = event.srcKey as string;

		// If the key starts with a cell value, then we're selecting
		// a view cell.
		if (key.startsWith("cell-")) {
			let cellId = parseInt(key.split("cell-")[1]);

			// Let the cell know it's been selected
			this.eventBus.$emit("focus-set", { viewCellId: cellId });
		}
	}

	public created() {
		this.eventBus.$on("maximise", this.toggleFullscreen);
		
		window.addEventListener("keydown", (event) => {
			if(this.selectedView.locked){
				event.stopPropagation()
			}
		}, true);
	}
	

	public mounted() {
		if (this.selectedView) {
			this.viewCells = this.selectedView.cells
				.filter(valid => valid.width && valid.height)
				.map(cell => {
					return {
						...cell,
						w: cell.width,
						h: cell.height,
						i: cell.viewCellId
					};
				});
		}
	}

	public beforeDestroy() {
		this.eventBus.$off("maximise", this.toggleFullscreen);
	}

	/**
	 * Handler for when the user chooses to full-screen a plugin.
	 * If the plugin raising the full-screen event is already full-screen, we
	 * reset cellIdToFullscreen back to 0 and 'minimize' it.
	 */
	public toggleFullscreen(viewCellId: number) {
		if (viewCellId == this.cellIdToFullscreen) {
			this.cellIdToFullscreen = 0;
			return;
		}

		this.cellIdToFullscreen = viewCellId;
	}

	/**
	 * Handler for when the user adds a plugin to the grid.
	 */
	private async finishAdd() {
		const parsedLayout = this.viewCells.map(cell => {
			return { ...cell, height: cell.h, width: cell.w, viewCellId: cell.i };
		});

		this.transientCellId -= 1;
		await this.setSelectedViewCells(parsedLayout);
		this.eventBus.$emit("finishedAdd", { tileType: this.getSelectedTileType });
	}

	/**
	 * Handler for when the user moves or resizes an existing plugin in the grid.
	 */
	public async updateLayout(newLayout) {
		const parsedLayout = newLayout.map(cell => {
			return { ...cell, height: cell.h, width: cell.w, viewCellId: cell.i };
		});
		await this.setSelectedViewCells(parsedLayout);
		
	}

	/**
	 * Handler for when the user selects a new plugin type to add,
	 * creates a stub view cell with the selected plugin type and
	 * a temporary ID.
	 */
	@Watch("getSelectedTileType")
	public onSelectedTileTypeChanged(value: any) {
		if (value && value.id) {
			this.cellToAdd = {
				pluginTypeId: value.id,
				viewCellId: this.transientCellId
			};
		} else {
			this.cellToAdd = null;
		}
	}

	/**
	 * Handler for when the cells within the current View are changed.
	 * 	- Updates our local copy of the view cells with the new data within
	 * the store.
	 *  - Resets our selectedTileType if we're no longer editing, or the user
	 * has reached their max number of a certain plugin type.
	 */
	@Watch("selectedView.cells")
	public onSelectedViewChanged(value: any) {
		if (value) {
			this.viewCells = value
				.filter(valid => valid.width && valid.height)
				.map(cell => {
					return {
						...cell,
						w: cell.width,
						h: cell.height,
						i: cell.viewCellId
					};
				});

			if (
				this.getSelectedTileType != null &&
				!this.editing &&
				this.getSelectedTileType.maxInstances <=
					this.viewCells.filter(cell => cell.pluginTypeId == this.getSelectedTileType.id).length
			) {
				this.setSelectedTileType(null);
			}
		}
	}
}
